/**
 * MODULE: _shared/frontend-shell/src/routes/builders.ts
 * PURPOSE: Centralized route path builders for consistent, safely-encoded navigation
 *
 * DEPENDENCIES: None
 *
 * PUBLIC API:
 *   - isLikelyAccountDetailId: True if the URL segment is a UUID (CRM or SI unified id)
 *   - buildCompanyDetailPath: Build /companies/:accountId (unified account id; encoded for safety)
 *   - buildAccountDetailPath: Build /accounts/:accountId (canonical account detail)
 *   - buildCrmAccountDetailPath: Alias of buildAccountDetailPath (legacy name)
 *   - buildCrmContactDetailPath: Build /crm/contacts/:contactId (CRM shell contact detail; full-page, no drawer)
 *
 * SIDE EFFECTS: None (pure functions)
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 39 / 150 lines
 *
 * RECENT: 2026-06-23 [2.2.1472] — Canonical account detail at **`/accounts/:id`**; **`/crm/accounts/:id`** redirects.
 * RECENT_PRIOR: [2.2.943] — **`buildCrmAccountDetailPath`** — root **`CHANGELOG` [2.2.943]`**
 *
 * See: /ARCHITECTURE.md
 *
 * CRM shell paths: canonical base **`/crm`** (**`ROUTES.CRM`** in **`src/config/routes.config.ts`**); nested
 * **`/crm/*`** children (home, territory, accounts detail, contacts, deals, pipeline-generation) are registered in **`src/App.tsx`**
 * (Prompt **1-D**). Prefer **`CRM_BASE_PATH`** / **`CRM_TABS`** from **`src/features/crm/constants/crmTabs.ts`** for tab links.
 */

/** True when the segment should load CompanyPage by unified account id (not legacy name redirect). */
export function isLikelyAccountDetailId(segment: string): boolean {
  const s = segment.trim();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s)) {
    return true;
  }
  // SI mock/dev insight ids from useInsights seed rows (acc-1 … acc-12).
  return /^acc-\d+$/i.test(s);
}

/**
 * Build the account detail path using unified account id (CRM `accounts.id` or SI-only `sales_insights.id`).
 * Legacy bookmarks may use company name; those hit AccountNameRedirect until replaced.
 */
export const buildCompanyDetailPath = (accountId: string): string =>
  `/companies/${encodeURIComponent(accountId)}`;

/** Canonical account detail — hub id or linked sales account id (never display name in the path). */
export const buildAccountDetailPath = (accountId: string): string =>
  `/accounts/${encodeURIComponent(accountId)}`;

/** @deprecated Prefer buildAccountDetailPath — kept for existing imports. */
export const buildCrmAccountDetailPath = buildAccountDetailPath;

/** CRM shell contact detail — full-page view; assistant rail stays clear (no side drawer). */
export const buildCrmContactDetailPath = (contactId: string): string =>
  `/crm/contacts/${encodeURIComponent(contactId)}`;

/** Full-page CRM deal detail — keeps shell AI assistant rail clear (parity with contact detail). */
export const buildCrmDealDetailPath = (opportunityId: string): string =>
  `/crm/deals/${encodeURIComponent(opportunityId)}`;
